
import React, { useRef, useState, useEffect, lazy, Suspense } from 'react';
import Button from '../../../components/ui/Button';
import { Play } from 'lucide-react';
import CampusBackground from './backgrounds/CampusBackground';

const HeroVideoModal = lazy(() => import('./HeroVideoModal'));

const Hero = () => {
      const carouselRef = useRef(null);
      const contentRef = useRef(null);
      const [isVideoOpen, setIsVideoOpen] = useState(null);

      useEffect(() => {
            const interval = setInterval(() => {
                  if (carouselRef.current) {
                        const container = carouselRef.current;
                        const firstChild = container.children[0];
                        if (!firstChild) return;
                        
                        // Calculate scroll amount (width of item + gap)
                        const gap = window.innerWidth >= 768 ? 80 : 48; // gap-12 is 48px, md:gap-20 is 80px
                        const scrollAmount = firstChild.offsetWidth + gap;
                        
                        // Check if we're near the end
                        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
                              container.scrollTo({ left: 0, behavior: 'auto' });
                        } else {
                              container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                        }
                  }
            }, 1000);

            return () => clearInterval(interval);
      }, []);

      const clients = [
            { name: 'Eureka School System', logo: '/assets/clients/eureka-school-system.webp' },
            { name: 'Indus Resource Center', logo: '/assets/clients/indus-resource-center.webp' },
            { name: 'Sukkur IBA University', logo: '/assets/clients/sukkur-iba.webp' },
            { name: 'Whales College', logo: '/assets/clients/whales-college-2.webp' },
            { name: 'Leadership School', logo: '/assets/clients/leadership-school.webp' },
            { name: 'Preparatory School Islamabad', logo: '/assets/clients/psi.webp' },
            { name: 'Links School', logo: '/assets/clients/links-school.webp' },
            { name: 'Scarsdale', logo: '/assets/clients/scarsdale.webp' },
            { name: 'Pakistan School Muscat', logo: '/assets/clients/pakistan-school-muscat.webp' },
            { name: 'Zicas School System', logo: '/assets/clients/zicas.webp' },
            { name: 'Badri High School', logo: '/assets/clients/badri-high-school.webp' },
            { name: 'Practical Schooling', logo: '/assets/clients/practical-schooling.webp' },
            { name: "L'ecole Mondiale", logo: '/assets/clients/lecole-mondiale.webp' },
            { name: 'Saifiyah Girls School', logo: '/assets/clients/saifiyah-girls-school.webp' },
            { name: 'Rayden College', logo: '/assets/clients/rayden-college.webp' },
            { name: "Wahaj Hussain's School System", logo: '/assets/clients/wahaj-hussain.webp' },
            { name: 'Trueman School System', logo: '/assets/clients/trueman-school.webp' },
            { name: 'MSB Educational Institute', logo: '/assets/clients/msb-educational.webp' },
            { name: 'Nordic International School', logo: '/assets/clients/nordic-international.webp' },
            { name: 'Dar-e-Arqam School', logo: '/assets/clients/dar-e-arqam.webp' },
            { name: 'Sindh Rural Support Organization', logo: '/assets/clients/srso.webp' },
            { name: "St. Gregory's High School", logo: '/assets/clients/sghs.webp' },
            { name: 'World Learning', logo: '/assets/clients/world-learning.webp' },
            { name: 'Adamjee', logo: '/assets/clients/adamjee.webp' },
            { name: 'Eden College', logo: '/assets/clients/eden-college.webp' },
            { name: 'Dewa Pakistan', logo: '/assets/clients/dewa-pakistan.webp' },
            { name: 'Maryam Montessori', logo: '/assets/clients/maryam-montessori.webp' },
            { name: 'Al Madina Islamic', logo: '/assets/clients/al-madina.webp' },
            { name: 'Origins', logo: '/assets/clients/origins.webp' }
      ];

      return (
            <section className="campus-hero relative flex flex-col items-center overflow-hidden bg-slate-50 dark:bg-[#0B0F19] text-slate-900 dark:text-white px-4 pt-14 md:pt-28 lg:pt-40 transition-colors duration-300">
                  <CampusBackground contentRef={contentRef} />

                  {/* Content spans the hero's full height so the client logos sit at its bottom
                      edge; the campus is drawn in the gap between the buttons and the logos. */}
                  <div ref={contentRef} className="relative z-10 w-full max-w-4xl mx-auto text-center flex flex-col flex-1">


                        {/* Main Heading */}
                        {/* --copy-offset is set by the campus scene so the actions sit just above the
                            rooftop sign; it is 0 until the scene has measured, so server and first
                            client render match. */}
                        <h1
                              className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-4 leading-[1.1] break-words [margin-top:var(--copy-offset,0px)] transition-[margin-top] duration-500 ease-out motion-reduce:transition-none"
                        >
                              <span className="bg-clip-text text-transparent bg-gradient-to-b from-slate-900 to-slate-700 dark:from-white dark:to-slate-400">
                                    AI-Powered LMS & School Management
                              </span>
                              <br className="hidden sm:block" />
                              <span className="text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl text-blue-600 dark:text-blue-400 mt-2 sm:mt-4 block">
                                    for Schools & Colleges
                              </span>
                        </h1>

                        {/* CTA Buttons */}
                        <div
                              style={{ animationDelay: '0.3s' }}
                              className="campus-actions hero-rise flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 w-full max-w-sm sm:max-w-none mx-auto"
                        >
                              <Button
                                    to="/request-free-trial"
                                    size="lg"
                                    className="w-full sm:w-auto px-6 sm:px-8 h-11 sm:h-12 rounded-xl font-medium transition-all duration-300 hover:scale-105"
                              >
                                    Request Free Trial
                              </Button>
                              <Button
                                    size="lg"
                                    variant="outline"
                                    onClick={() => setIsVideoOpen(true)}
                                    className="w-full sm:w-auto px-6 sm:px-8 h-11 sm:h-12 rounded-xl font-medium transition-all duration-300 hover:scale-105 group"
                              >
                                    <Play className="mr-2 h-4 w-4 fill-current opacity-70 group-hover:opacity-100 transition-opacity" />
                                    Watch Demo
                              </Button>
                        </div>

                        {/* Client Logos */}
                        <div
                              style={{ animationDelay: '0.4s' }}
                              className="hero-rise space-y-3 mt-auto"
                        >
                              <p className="text-sm text-primary dark:text-blue-400 font-medium uppercase tracking-wide">
                                    Powering the best educational institutions
                              </p>

                              <div className="relative flex items-center">


                                    {/* Scroll Container */}
                                    <div 
                                          ref={carouselRef}
                                          className="flex overflow-x-auto gap-12 md:gap-20 px-6 py-2 no-scrollbar scroll-smooth snap-x snap-mandatory mask-image-fade"
                                          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)' }}
                                    >
                                          {clients.map((client, index) => (
                                                <div
                                                      key={index}
                                                      className="group flex-shrink-0 relative p-3 rounded-xl transition-all duration-300 hover:bg-blue-500/10 hover:shadow-[0_0_20px_rgba(59,130,246,0.2)] snap-center"
                                                      title={client.name}
                                                >
                                                      <img
                                                            src={client.logo}
                                                            alt={client.name}
                                                            width="64" height="64" loading="lazy" decoding="async"
                                                            className="h-12 md:h-14 w-auto object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300 mix-blend-multiply dark:mix-blend-screen"
                                                      />
                                                </div>
                                          ))}
                                    </div>


                              </div>
                        </div>
                  </div>

                  {/* Video modal is loaded on demand — see HeroVideoModal */}
                  {isVideoOpen !== null && (
                        <Suspense fallback={null}>
                              <HeroVideoModal isVideoOpen={isVideoOpen} setIsVideoOpen={setIsVideoOpen} />
                        </Suspense>
                  )}
            </section>
      );
};

export default Hero;

